import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, X, Sparkles, Image as ImageIcon } from 'lucide-react';
import { api } from '../services/api';
import { IOS_SPRING } from '../constants';

// ── v083 user memory: the injected brief + distilled typed rows + recent
//    episodic sessions (log → reflect → inject). Shared by the playground
//    Memory tab AND the Settings profile page. Read-mostly; a row can be forgotten.

const TYPE_META = {
  identity:     { title: 'About you',            color: '#0A84FF' },
  preference:   { title: 'Preferences',          color: '#5E5CE6' },
  constraint:   { title: 'Important constraints', color: '#FF3B30' },
  goal:         { title: 'Goals',                color: '#AF52DE' },
  instruction:  { title: 'How I should respond', color: '#FF9F0A' },
  relationship: { title: 'People & things',      color: '#32ADE6' },
  domain_fact:  { title: 'Widget details',       color: '#34C759' },
};
const TYPE_ORDER = ['identity', 'preference', 'constraint', 'goal', 'instruction', 'relationship', 'domain_fact'];

export default function MemoryContent({ memoryUpdatedAt: externalUpdate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setData(await api.getMemoryProfile());
    } catch (e) {
      console.error('Failed to load memory:', e);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { setLoading(true); load(); }, [load]);
  // Reload when SSE signals a memory update.
  useEffect(() => { if (externalUpdate) load(); }, [externalUpdate, load]);

  const handleForget = useCallback(async (id) => {
    setData(prev => (prev ? { ...prev, memories: prev.memories.filter(m => m.id !== id) } : prev));
    try {
      await api.forgetMemoryRow(id);
    } catch (e) {
      console.error('Failed to forget memory row:', e);
      load();
    }
  }, [load]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin" style={{ color: 'rgba(0,0,0,0.15)' }} />
      </div>
    );
  }

  const memories = data?.memories || [];
  const logs = data?.session_logs || [];
  const brief = (data?.brief || '').trim();
  const grouped = TYPE_ORDER
    .map(t => ({ type: t, ...TYPE_META[t], items: memories.filter(m => m.type === t) }))
    .filter(g => g.items.length);

  if (!brief && !memories.length && !logs.length) {
    return (
      <Card>
        <div style={{ padding: '28px 16px', textAlign: 'center', color: 'rgba(0,0,0,0.35)' }}>
          <Sparkles size={22} style={{ opacity: 0.4, marginBottom: 8 }} />
          <div style={{ fontSize: 14, color: 'rgba(0,0,0,0.5)' }}>No memory yet</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>As you chat, VI remembers what matters — it'll show up here.</div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {brief && (
        <Card>
          <SectionTitle>What VI remembers about you</SectionTitle>
          <div style={{
            margin: '0 16px 14px', padding: '10px 12px', borderRadius: 12,
            background: 'rgba(10,132,255,0.05)', fontSize: 13.5, lineHeight: 1.6,
            color: 'rgba(0,0,0,0.78)', whiteSpace: 'pre-wrap',
          }}>
            {brief}
          </div>
        </Card>
      )}

      {grouped.map((g, gi) => (
        <Card key={g.type} delay={gi * 0.03}>
          <SectionTitle color={g.color}>{g.title}</SectionTitle>
          <div style={{ padding: '0 16px 8px' }}>
            <AnimatePresence initial={false}>
              {g.items.map(m => (
                <MemoryRow key={m.id} m={m} color={g.color} onForget={() => handleForget(m.id)} />
              ))}
            </AnimatePresence>
          </div>
        </Card>
      ))}

      {logs.length > 0 && (
        <Card delay={0.05}>
          <SectionTitle>Recent sessions</SectionTitle>
          <div style={{ padding: '0 16px 10px' }}>
            {logs.slice(0, 10).map(l => <SessionRow key={l.id} l={l} />)}
          </div>
        </Card>
      )}
    </div>
  );
}

// ── shells ──
function Card({ children, delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, ...IOS_SPRING }}
      style={{
        background: '#fff', borderRadius: 16,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 0 0 0.5px rgba(0,0,0,0.03)',
        overflow: 'hidden',
      }}
    >
      {children}
    </motion.div>
  );
}

function SectionTitle({ children, color }) {
  return (
    <div className="flex items-center gap-2 px-4 pt-3 pb-1.5">
      {color && <span style={{ width: 7, height: 7, borderRadius: 4, background: color, display: 'inline-block' }} />}
      <span className="font-semibold" style={{ fontSize: 14, color: '#000' }}>{children}</span>
    </div>
  );
}

function MemoryRow({ m, color, onForget }) {
  const pol = m.polarity === 'positive' ? 'likes' : m.polarity === 'negative' ? 'avoids' : null;
  return (
    <motion.div
      layout
      exit={{ opacity: 0, height: 0, marginTop: 0, marginBottom: 0 }}
      transition={IOS_SPRING}
      className="group flex items-start gap-2 py-1.5"
      style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}
    >
      <span className="shrink-0" style={{ color: color || 'rgba(0,0,0,0.2)', fontSize: 10, lineHeight: '20px' }}>●</span>
      <div className="flex-1" style={{ fontSize: 14, color: 'rgba(0,0,0,0.78)', lineHeight: 1.5 }}>
        {m.content}
        {pol && (
          <span className="ml-1.5 px-1.5 py-0.5 rounded-full align-middle" style={{
            fontSize: 10, fontWeight: 500,
            color: m.polarity === 'positive' ? 'rgb(34,197,94)' : 'rgb(255,59,48)',
            background: m.polarity === 'positive' ? 'rgba(34,197,94,0.08)' : 'rgba(255,59,48,0.08)',
          }}>{pol}</span>
        )}
        {m.sensitivity === 'sensitive' && (
          <span className="ml-1 px-1.5 py-0.5 rounded-full align-middle" style={{
            fontSize: 10, fontWeight: 500, color: 'rgb(255,149,0)', background: 'rgba(255,149,0,0.1)',
          }}>sensitive</span>
        )}
        {m.domain && (
          <span className="ml-1 align-middle" style={{ fontSize: 11, color: 'rgba(0,0,0,0.3)' }}>· {m.domain}</span>
        )}
      </div>
      <button
        onClick={onForget}
        title="Forget this"
        className="shrink-0 p-1 rounded-full opacity-0 group-hover:opacity-100 hover:bg-black/[0.05] transition-opacity"
        style={{ color: 'rgba(0,0,0,0.3)' }}
      >
        <X size={13} />
      </button>
    </motion.div>
  );
}

function SessionRow({ l }) {
  const date = l.created_at ? new Date(l.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
  const media = Array.isArray(l.media) ? l.media : [];
  return (
    <div className="flex items-start gap-2.5 py-2" style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
      <span className="shrink-0" style={{ fontSize: 11, color: 'rgba(0,0,0,0.3)', width: 42, lineHeight: '18px' }}>{date}</span>
      <div className="flex-1 min-w-0">
        <div style={{ fontSize: 13, color: 'rgba(0,0,0,0.7)', lineHeight: 1.45 }}>{l.summary}</div>
        {media.length > 0 && (
          <div className="flex flex-wrap items-center gap-1 mt-1">
            {media.map((md, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md" style={{
                fontSize: 10.5, color: 'rgba(0,0,0,0.45)', background: 'rgba(0,0,0,0.035)',
              }}>
                <ImageIcon size={10} style={{ opacity: 0.6 }} />
                {md.desc || md.kind || 'media'}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
